// Step 4 — interactive 3D truck loading.
//
// Maintains a mutable `placements` array (one entry per load item) that the
// user can edit by dragging in 3D, rotating via the HTML overlay on the
// selected item, or removing. The placer runs once on entry to give a sane
// starting layout; after that, everything comes from this component's state.

import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Table, Badge, Alert, Button } from 'react-bootstrap';

import { packIntoTruck } from '../../../features/warehouse/truckPacker.js';
import {
  itemFootprint,
  findPlacementCollisions,
  placementFitsTruck,
  placementOnTruckBed,
} from '../../../features/warehouse/warehouseUtils.js';
import SimTruckLoad3D from '../SimTruckLoad3D.js';

const STAGING_OFFSET_Z = -2.5;  // where newly-staged items land along Z

export default function StepSimulate({ truck, items }) {
  const [placements, setPlacements] = useState([]);

  // Re-seed placements whenever the upstream items / truck change.
  useEffect(() => {
    setPlacements(buildInitialPlacements(items, truck));
  }, [items, truck]);

  // ---- Callbacks the 3D view talks to --------------------------------------

  const updatePlacement = (id, patch) => {
    setPlacements(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)));
  };

  const removePlacement = (id) => {
    setPlacements(prev => prev.filter(p => p.id !== id));
  };

  const autoPack = () => {
    setPlacements(buildInitialPlacements(items, truck));
  };

  const sendToStaging = (id) => {
    // Find a z spot in the staging area that doesn't already have an item.
    setPlacements(prev => {
      const me = prev.find(p => p.id === id);
      if (!me) return prev;
      const stagingX = stagingNextX(prev.filter(p => p.id !== id), itemFootprint(me.item, me.orientation));
      return prev.map(p =>
        p.id === id ? { ...p, x: stagingX, z: STAGING_OFFSET_Z } : p
      );
    });
  };

  // ---- Stats + validation from the live placements ------------------------

  const stats = useMemo(() => computeStats(placements, truck), [placements, truck]);
  const collidingIds = useMemo(() => findPlacementCollisions(placements), [placements]);
  const outOfBoundsIds = useMemo(() => {
    const s = new Set();
    for (const p of placements) {
      if (placementOnTruckBed(p, truck) && !placementFitsTruck(p, truck)) s.add(p.id);
    }
    return s;
  }, [placements, truck]);
  const invalidCount = new Set([...collidingIds, ...outOfBoundsIds]).size;

  if (items.length === 0) {
    return <Alert variant="warning">No items in the load list — go back and add some.</Alert>;
  }

  return (
    <Row>
      <Col md={9}>
        <Card>
          <Card.Body style={{ position: 'relative' }}>
            <SimTruckLoad3D
              truck={truck}
              placements={placements}
              onMove={(id, { x, z }) => updatePlacement(id, { x, z })}
              onRotate={(id, nextO) => updatePlacement(id, { orientation: nextO })}
              onRemove={removePlacement}
            />
          </Card.Body>
        </Card>
      </Col>

      <Col md={3}>
        <Card className="mb-3">
          <Card.Header className="d-flex align-items-center justify-content-between">
            <strong>Capacity</strong>
            <Button size="sm" variant="outline-primary" type="button" onClick={autoPack}>
              Auto-pack
            </Button>
          </Card.Header>
          <Card.Body>
            <Stat label="Items on truck" value={`${stats.onTruck} / ${placements.length}`} />
            <Stat label="In staging" value={stats.inStaging} />
            <Stat
              label="Floor area"
              value={`${stats.floorUsagePct}%`}
              pct={stats.floorUsagePct}
            />
            <Stat
              label="Weight"
              value={`${stats.weightKgOnTruck.toFixed(0)} kg`}
              sub={truck.maxPayloadKg ? `/ ${truck.maxPayloadKg} kg max` : null}
              danger={truck.maxPayloadKg && stats.weightKgOnTruck > truck.maxPayloadKg}
            />
          </Card.Body>
        </Card>

        {invalidCount > 0 && (
          <Alert variant="danger" className="py-2 mb-3">
            <strong>{invalidCount} placement{invalidCount > 1 ? 's' : ''} invalid.</strong>{' '}
            {collidingIds.size > 0 && `${collidingIds.size} overlap${collidingIds.size > 1 ? '' : 's'} another item. `}
            {outOfBoundsIds.size > 0 && `${outOfBoundsIds.size} spill${outOfBoundsIds.size > 1 ? '' : 's'} past the truck walls. `}
            Drag the red items to resolve.
          </Alert>
        )}

        <Card>
          <Card.Header><strong>Placements</strong></Card.Header>
          <Card.Body className="p-0" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <Table size="sm" className="mb-0">
              <tbody>
                {placements.map(p => {
                  const on = isInTruck(p, truck);
                  const invalid = collidingIds.has(p.id) || outOfBoundsIds.has(p.id);
                  return (
                    <tr
                      key={p.id}
                      className={invalid ? 'table-danger' : undefined}
                    >
                      <td>
                        <div className="fw-semibold small">
                          <code>{p.item.serial}</code>
                        </div>
                        <div className="small text-muted">{p.item.cableName}</div>
                        {invalid && (
                          <div className="small text-danger">
                            {collidingIds.has(p.id) && 'Overlap'}
                            {collidingIds.has(p.id) && outOfBoundsIds.has(p.id) && ' · '}
                            {outOfBoundsIds.has(p.id) && 'Outside truck'}
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        <Badge bg={on ? 'success' : 'secondary'}>
                          {on ? 'on truck' : 'staging'}
                        </Badge>
                        {on && (
                          <div className="mt-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => sendToStaging(p.id)}
                            >
                              Unload
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

// ---------------------------------------------------------------------------

function Stat({ label, value, pct, sub, danger }) {
  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between small">
        <span className="text-muted">{label}</span>
        <strong className={danger ? 'text-danger' : undefined}>{value}</strong>
      </div>
      {sub && <div className="small text-muted text-end">{sub}</div>}
      {Number.isFinite(pct) && (
        <div className="progress mt-1" style={{ height: 4 }}>
          <div
            className={`progress-bar ${pct > 100 ? 'bg-danger' : 'bg-primary'}`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function buildInitialPlacements(items, truck) {
  const packed = packIntoTruck(items, truck);
  const onTruck = packed.placed.map(p => ({
    id: p.item._id,
    item: p.item,
    x: p.x,
    z: p.z,
    y: 0,
    orientation: p.orientation || p.item.orientation,
  }));

  // Overflow items land in the staging area, spaced along the truck length.
  let stagingCursor = 0;
  const staging = packed.overflow.map(o => {
    const fp = itemFootprint(o.item, o.item.orientation);
    const x = stagingCursor + (fp?.lengthM || 1) / 2 + 0.2;
    stagingCursor = x + (fp?.lengthM || 1) / 2;
    return {
      id: o.item._id,
      item: o.item,
      x,
      z: STAGING_OFFSET_Z,
      y: 0,
      orientation: o.item.orientation,
    };
  });

  return [...onTruck, ...staging];
}

function stagingNextX(otherPlacements, fp) {
  // Find the rightmost staged item and append after it.
  const staged = otherPlacements.filter(p => p.z < 0);
  if (staged.length === 0) return (fp?.lengthM || 1) / 2 + 0.2;
  const maxRight = staged.reduce((mx, p) => {
    const f = itemFootprint(p.item, p.orientation);
    return Math.max(mx, p.x + (f?.lengthM || 1) / 2);
  }, 0);
  return maxRight + (fp?.lengthM || 1) / 2 + 0.2;
}

function isInTruck(p, truck) {
  return p.x >= 0 && p.x <= truck.lengthM && p.z >= 0 && p.z <= truck.widthM;
}

function computeStats(placements, truck) {
  let onTruck = 0;
  let weightKgOnTruck = 0;
  let footprintAreaOnTruck = 0;
  for (const p of placements) {
    if (isInTruck(p, truck)) {
      onTruck++;
      weightKgOnTruck += Number(p.item.weightKg) || 0;
      const fp = itemFootprint(p.item, p.orientation);
      if (fp) footprintAreaOnTruck += fp.lengthM * fp.widthM;
    }
  }
  const bedArea = truck.lengthM * truck.widthM;
  return {
    onTruck,
    inStaging: placements.length - onTruck,
    weightKgOnTruck,
    floorUsagePct: bedArea > 0
      ? Math.min(100, Math.round((footprintAreaOnTruck / bedArea) * 100))
      : 0,
  };
}
